import { useEffect, useState } from "react";
import { CalendarClock, History } from "lucide-react";
import { API } from "@/lib/api";
import { findCrest, getCrestMap } from "@/lib/crest";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
};

const TeamName = ({ name, crests, right }) => {
  const isSporting = name?.includes("Sporting");
  const crest = findCrest(crests, name);
  return (
    <span className={`flex min-w-0 items-center gap-2 ${right ? "flex-row-reverse" : ""}`}>
      {crest && (
        <img
          src={crest}
          alt={`Escudo de ${name}`}
          className="h-8 w-8 shrink-0 object-contain"
          data-testid={`strip-crest-${name?.toLowerCase().replace(/\s+/g, "-")}`}
        />
      )}
      <span
        className={`truncate font-display text-lg font-extrabold uppercase sm:text-xl ${
          isSporting ? "text-glow" : "text-white"
        }`}
      >
        {name}
      </span>
    </span>
  );
};

const SportingMatch = () => {
  const [data, setData] = useState(null);
  const [crests, setCrests] = useState({});

  useEffect(() => {
    API.get("/sporting/match").then(({ data }) => setData(data)).catch(() => {});
    getCrestMap().then(setCrests);
  }, []);

  if (!data || (!data.next && !data.last)) return null;
  const { next, last } = data;

  return (
    <section className="border-b border-white/10 bg-ink" data-testid="sporting-match-strip">
      <div className="mx-auto grid max-w-7xl gap-4 px-5 py-8 md:grid-cols-2 md:px-12">
        {last && last.home && (
          <div className="glass relative overflow-hidden rounded-2xl p-5 sm:p-6" data-testid="sporting-last-result">
            <div className="stripes-rojiblancas absolute inset-x-0 top-0 h-1 opacity-80" />
            <p className="flex items-center gap-2 font-mono-score text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
              <History className="h-3.5 w-3.5 text-crimson" /> Último resultado
            </p>
            <p className="mt-1 font-mono-score text-[10px] uppercase tracking-widest text-slate-500">
              {last.competition} · {fmtDate(last.date)}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <TeamName name={last.home} crests={crests} />
              </div>
              <div className="shrink-0 rounded-lg bg-ink px-3 py-1.5 font-mono-score text-xl font-bold text-white">
                <span>{last.home_score ?? "–"}</span>
                <span className="mx-1 text-crimson">:</span>
                <span>{last.away_score ?? "–"}</span>
              </div>
              <div className="min-w-0 flex-1 text-right">
                <TeamName name={last.away} crests={crests} right />
              </div>
            </div>
          </div>
        )}
        {next && next.home && (
          <div className="glass relative overflow-hidden rounded-2xl p-5 sm:p-6" data-testid="sporting-next-match">
            <div className="stripes-rojiblancas absolute inset-x-0 top-0 h-1 opacity-80" />
            <p className="flex items-center gap-2 font-mono-score text-[10px] font-semibold uppercase tracking-[0.25em] text-glow">
              <CalendarClock className="h-3.5 w-3.5" /> Próximo partido
            </p>
            <p className="mt-1 font-mono-score text-[10px] uppercase tracking-widest text-slate-500">
              {next.competition} · {fmtDate(next.date)}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <TeamName name={next.home} crests={crests} />
              </div>
              <div className="shrink-0 rounded-lg bg-crimson px-3 py-1.5 font-display text-sm font-extrabold uppercase tracking-wider text-white">
                VS
              </div>
              <div className="min-w-0 flex-1 text-right">
                <TeamName name={next.away} crests={crests} right />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SportingMatch;
